// src/pages/superadmin/order/CreateOrder.jsx
import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  TextField,
  MenuItem,
  Button,
  Box,
  Table,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import api from "../../../utils/api";
import SuperAdminLayout from "../../../layouts/SuperAdminLayout";

const CreateOrder = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const superAdminId = user?.id;
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [form, setForm] = useState({
    customer: "",
    challanNumber: "",
    date: new Date().toISOString().split("T")[0],
    items: [
      {
        product: "",
        unit: "",
        availableQty: 0,
        secondaryQuantity: 0,
        quantity: 0,
        price: 0,
      },
    ],
  });

  // Dialog states & new-entity forms (copy your dialogs from SChallan)
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [openUnitDialog, setOpenUnitDialog] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    /* … */
  });
  const [newProduct, setNewProduct] = useState({
    /* … */
  });
  const [newUnit, setNewUnit] = useState("");

  // Compute total
  const getTotal = () =>
    form.items.reduce((sum, item) => sum + item.quantity * item.price, 0);

  // Initial fetch: customers, products+stock, units, count
  useEffect(() => {
    async function load() {
      const [custRes, prodRes, stockRes, countRes, unitRes] = await Promise.all(
        [
          api.get(`/api/customers/get-all/${superAdminId}`),
          api.get(`/api/products/get-all/${superAdminId}`),
          api.get(`/api/reports/closing-stock/${superAdminId}`),
          api.get(`/api/orders/count/${superAdminId}`),
          api.get(`/api/units/get-all/${superAdminId}`),
        ]
      );

      setCustomers(custRes.data);
      setUnits(unitRes.data);

      // merge stock
      const stockMap = {};
      stockRes.data.forEach((s) => {
        stockMap[s.productName] = s.closingQty;
      });
      const prods = prodRes.data.map((p) => ({
        ...p,
        closingQty: stockMap[p.productName] ?? 0,
      }));
      setProducts(prods);

      setForm((f) => ({
        ...f,
        challanNumber: String((countRes.data.count ?? 0) + 1).padStart(3, "0"),
      }));
    }
    if (superAdminId) load();
  }, [superAdminId]);

  // Handle changes in each item row
  const handleItemChange = (idx, key, value) => {
    const items = [...form.items];
    const item = items[idx];

    if (key === "product") {
      const p = products.find((x) => x.productName === value);
      if (p) {
        item.product = p.productName;
        item.unit = p.primaryUnit;
        item.price = p.minSalePrice || 0;
        item.availableQty = p.closingQty || 0;
        item.secondaryQuantity = 0;
        item.quantity = 0;
        item._secFactor = p.secondaryQuantity || 1;
      }
    } else if (key === "secondaryQuantity") {
      item.secondaryQuantity = value;
      item.quantity = parseFloat((value * (item._secFactor || 1)).toFixed(2));
    } else if (key === "quantity") {
      item.quantity = value;
      item.secondaryQuantity = parseFloat(
        (value / (item._secFactor || 1)).toFixed(2)
      );
    } else if (key === "price") {
      item.price = value;
    }

    items[idx] = item;
    setForm({ ...form, items });
  };

  const addRow = () => {
    setForm((f) => ({
      ...f,
      items: [
        ...f.items,
        {
          product: "",
          unit: "",
          availableQty: 0,
          secondaryQuantity: 0,
          quantity: 0,
          price: 0,
        },
      ],
    }));
  };

  const removeRow = (idx) => {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async () => {
    // ensure price defined
    for (const itm of form.items) {
      if (!itm.price) return alert(`❌ Price missing for ${itm.product}`);
    }
    try {
      await api.post("/api/orders/create", {
        ...form,
        superAdminId,
        total: getTotal(),
      });
      alert("Order created");
      navigate("/super-admin/order/view");
    } catch (err) {
      console.error(err);
      alert("Failed to create order");
    }
  };

  return (
    <SuperAdminLayout>
      {/* … your existing dialogs for creating Customer/Product/Unit … */}

      <Container
        maxWidth="lg"
        sx={{
          mt: 4,
          mb: 4,
          p: 4,
          bgcolor: "#fff",
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <Typography variant="h5" align="center" gutterBottom>
          Create Order
        </Typography>

        {/* Header */}
        <Box display="flex" gap={2} mb={3}>
          <TextField
            select
            label="Customer"
            sx={{ width: 300 }}
            value={form.customer}
            onChange={(e) =>
              setForm((f) => ({ ...f, customer: e.target.value }))
            }
          >
            {customers.map((c) => (
              <MenuItem key={c._id} value={c.partyName}>
                {c.partyName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Order No"
            sx={{ width: 300 }}
            value={form.challanNumber}
            onChange={(e) =>
              setForm((f) => ({ ...f, challanNumber: e.target.value }))
            }
          />
          <TextField
            type="date"
            label="Date"
            sx={{ width: 300 }}
            InputLabelProps={{ shrink: true }}
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
          />
        </Box>

        {/* Items Table */}
        <Paper elevation={2} sx={{ mb: 3 }}>
          <Table>
            <TableBody>
              {form.items.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <TextField
                      select
                      fullWidth
                      label="Product"
                      size="small"
                      value={item.product}
                      onChange={(e) =>
                        handleItemChange(idx, "product", e.target.value)
                      }
                    >
                      {products.map((p) => (
                        <MenuItem key={p._id} value={p.productName}>
                          {p.productName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>

                  <TableCell>
                    <TextField
                      fullWidth
                      label="Unit"
                      size="small"
                      value={item.unit}
                      InputProps={{ readOnly: true }}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      type="number"
                      fullWidth
                      label="Avail Stock"
                      size="small"
                      value={item.availableQty}
                      InputProps={{ readOnly: true }}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      type="number"
                      fullWidth
                      label="Secondary Qty"
                      size="small"
                      value={item.secondaryQuantity}
                      onChange={(e) =>
                        handleItemChange(
                          idx,
                          "secondaryQuantity",
                          Number(e.target.value)
                        )
                      }
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      type="number"
                      fullWidth
                      label="Primary Qty"
                      size="small"
                      value={item.quantity}
                      onChange={(e) =>
                        handleItemChange(
                          idx,
                          "quantity",
                          Number(e.target.value)
                        )
                      }
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      type="number"
                      fullWidth
                      label="Price"
                      size="small"
                      value={item.price}
                      onChange={(e) =>
                        handleItemChange(idx, "price", Number(e.target.value))
                      }
                    />
                  </TableCell>

                  <TableCell>
                    ₹{(item.quantity * item.price).toFixed(2)}
                  </TableCell>

                  <TableCell>
                    <IconButton onClick={() => removeRow(idx)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        {/* Footer controls */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Button variant="outlined" onClick={addRow}>
            + Add Row
          </Button>
          <Typography variant="h6">Total: ₹{getTotal().toFixed(2)}</Typography>
        </Box>

        <Box textAlign="center" mt={4}>
          <Button variant="contained" onClick={handleSubmit}>
            Save Order
          </Button>
        </Box>
      </Container>
    </SuperAdminLayout>
  );
};

export default CreateOrder;
